import { useState } from "react";
import { Radio, Mic, Cloud, Megaphone, Music2, Loader2, Copy, Check, Sparkles } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

const API = BACKEND_URL;

const CONTENT_TYPES = [
  { id: "intro", label: "Show Intro", icon: Mic, desc: "Open the show — welcome listeners, tease what's coming.", color: "from-violet-500/15 to-fuchsia-500/15", border: "border-violet-500/25" },
  { id: "station_id", label: "Station ID", icon: Radio, desc: "Short branded drop with the station name.", color: "from-cyan-500/15 to-blue-500/15", border: "border-cyan-500/25" },
  { id: "music_break", label: "Music Break", icon: Music2, desc: "Smooth transition between songs.", color: "from-emerald-500/15 to-teal-500/15", border: "border-emerald-500/25" },
  { id: "weather", label: "Weather Update", icon: Cloud, desc: "On-air weather report for a location.", color: "from-sky-500/15 to-blue-500/15", border: "border-sky-500/25" },
  { id: "sponsor", label: "Sponsor Spot", icon: Megaphone, desc: "Read for a sponsor or ad partner.", color: "from-amber-500/15 to-orange-500/15", border: "border-amber-500/25" },
];

const TONES = ["energetic", "smooth", "professional", "casual", "hype", "warm"];

export const AIRadioHost = ({ authToken }) => {
  const [type, setType] = useState("intro");
  const [form, setForm] = useState({
    station_name: "",
    dj_name: "",
    show_name: "",
    tone: "energetic",
    duration_seconds: 20,
    extra_context: "",
    location: "",
    sponsor_name: "",
    sponsor_message: "",
  });
  const [generating, setGenerating] = useState(false);
  const [output, setOutput] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    setGenerating(true); setError(""); setOutput("");
    try {
      const r = await fetch(`${API}/api/ai/broadcast/host/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ content_type: type, ...form }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Generation failed");
      setOutput(d.content || "");
    } catch (e) {
      setError(e.message);
    }
    setGenerating(false);
  };

  const copy = async () => {
    if (!output) return;
    await navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const current = CONTENT_TYPES.find((c) => c.id === type);

  return (
    <div data-testid="ai-radio-host">
      <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
        <Sparkles className="w-5 h-5 text-cyan-400" /> AI Radio Host
      </h3>

      {/* Content type selector */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {CONTENT_TYPES.map((t) => (
          <button key={t.id} onClick={() => setType(t.id)}
            className={`p-4 rounded-2xl border text-left transition ${
              type === t.id
                ? `bg-gradient-to-br ${t.color} ${t.border} ring-1 ring-cyan-400/30`
                : "bg-white/[0.02] border-white/5 hover:border-white/15"
            }`}
            data-testid={`host-type-${t.id}`}>
            <t.icon className={`w-5 h-5 mb-2 ${type === t.id ? "text-white" : "text-white/50"}`} />
            <p className={`text-xs font-semibold ${type === t.id ? "text-white" : "text-white/70"}`}>{t.label}</p>
            <p className="text-white/30 text-[10px] mt-1 leading-tight">{t.desc}</p>
          </button>
        ))}
      </div>

      {/* Form */}
      <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-white/50 text-xs block mb-1.5">Station name</label>
            <input value={form.station_name} onChange={(e) => setForm({ ...form, station_name: e.target.value })}
              placeholder="e.g. 106.3 ATL The Beat"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500/50"
              data-testid="host-station-name" />
          </div>
          {(type === "intro" || type === "music_break") && (
            <div>
              <label className="text-white/50 text-xs block mb-1.5">DJ / Host name</label>
              <input value={form.dj_name} onChange={(e) => setForm({ ...form, dj_name: e.target.value })}
                placeholder="e.g. DJ Snap"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500/50" />
            </div>
          )}
          {type === "intro" && (
            <div>
              <label className="text-white/50 text-xs block mb-1.5">Show name</label>
              <input value={form.show_name} onChange={(e) => setForm({ ...form, show_name: e.target.value })}
                placeholder="e.g. Morning Drive"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500/50" />
            </div>
          )}
          {type === "weather" && (
            <div>
              <label className="text-white/50 text-xs block mb-1.5">Location</label>
              <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="e.g. Atlanta, GA"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500/50" />
            </div>
          )}
          {type === "sponsor" && (
            <>
              <div>
                <label className="text-white/50 text-xs block mb-1.5">Sponsor name</label>
                <input value={form.sponsor_name} onChange={(e) => setForm({ ...form, sponsor_name: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500/50" />
              </div>
              <div className="md:col-span-2">
                <label className="text-white/50 text-xs block mb-1.5">Sponsor message / offer</label>
                <input value={form.sponsor_message} onChange={(e) => setForm({ ...form, sponsor_message: e.target.value })}
                  placeholder="e.g. 20% off with code RADIO20"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500/50" />
              </div>
            </>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <label className="text-white/50 text-xs block mb-1.5">Tone</label>
            <select value={form.tone} onChange={(e) => setForm({ ...form, tone: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500/50">
              {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="text-white/50 text-xs block mb-1.5">Duration (sec)</label>
            <input type="number" min="5" max="90" value={form.duration_seconds}
              onChange={(e) => setForm({ ...form, duration_seconds: parseInt(e.target.value) || 20 })}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500/50" />
          </div>
        </div>

        {(type === "intro" || type === "music_break") && (
          <div>
            <label className="text-white/50 text-xs block mb-1.5">Extra context (optional)</label>
            <textarea value={form.extra_context} onChange={(e) => setForm({ ...form, extra_context: e.target.value })}
              rows={2} placeholder="e.g. Tease a live event at 8 PM"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500/50 resize-y" />
          </div>
        )}

        {error && <p className="text-red-400 text-sm">{error}</p>}

        <button onClick={generate} disabled={generating}
          className="w-full flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-black py-2.5 rounded-lg text-sm font-semibold transition disabled:opacity-50"
          data-testid="host-generate">
          {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating {current.label}...</> : <><Sparkles className="w-4 h-4" /> Generate {current.label}</>}
        </button>
      </div>

      {output && (
        <div className="mt-4 bg-white/[0.03] border border-cyan-500/20 rounded-2xl p-5" data-testid="host-output">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-white font-semibold text-sm flex items-center gap-2">
              <current.icon className="w-4 h-4 text-cyan-400" /> {current.label}
            </h4>
            <button onClick={copy} className="flex items-center gap-1.5 text-white/50 hover:text-white text-xs transition">
              {copied ? <><Check className="w-3.5 h-3.5 text-emerald-400" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
            </button>
          </div>
          <p className="text-white/90 text-sm leading-relaxed whitespace-pre-wrap">{output}</p>
          <p className="text-white/30 text-[10px] mt-3">
            ~{output.split(/\s+/).filter(Boolean).length} words · read time ~{Math.round(output.split(/\s+/).filter(Boolean).length / 3)}s
          </p>
        </div>
      )}
    </div>
  );
};

export default AIRadioHost;
